/**
 * pyramidPlot.ts - the plot a body with no surveyed region stands on.
 *
 * `sites.json` carries a region bake for luna and mars and for nothing else,
 * so on the other nineteen descendable bodies there was no claim to load, no
 * `PlotField` to build a mesh from, and the ladder ended at the pyramid's
 * floor with no walkaround. This resamples the quadtree leaves the descent
 * already has resident under the landing point into the same `PlotField` a
 * region bake produces, so the mesh, the survey, the rocks and the walkaround
 * mount from ground that is already on screen.
 *
 * It invents no relief and no imagery: the field is the leaves' own height
 * over the patch, and its orthoimage the luminance of their own drawn albedo,
 * and nothing else, which is why the meta it writes reports the
 * level that answered as the source resolution. A body whose pyramid carries
 * no height role has nothing here at all (`docs/ATLAS-TERRAIN.md`).
 */

import {
  PlotField, buildHeightMips,
  type FieldBuffers, type HexPlacement, type PlotMeta, type SourceClass,
} from './plotField.ts';

/** Samples across the patch, each way. The mesh lattice resamples this again. */
export const PYRAMID_PLOT_SAMPLES = 128;
/** The parent hex, metres: the radius every baked region uses (`bake_region.py`). */
export const PYRAMID_PLOT_HEX_RADIUS_M = 1200;
/** Below this share of the patch resolved, the ring has not got the ground yet. */
export const PYRAMID_PLOT_MIN_RESOLVED = 0.9;
/** The orthoimage's texels across the patch, each way. */
export const PYRAMID_PLOT_ORTHO_SAMPLES = 512;

/** Luminance of the drawn imagery at a body-fixed point, 0..255; null where the
 *  imagery drawn there is unmeasured ground, undefined where none is drawn yet. */
export type PyramidAlbedoProbe = (latDeg: number, lonDeg: number) => number | null | undefined;

/** Elevation at a body-fixed point, metres above the datum, with the level that
 *  answered - or null where no resident leaf covers it. */
export type PyramidHeightProbe = (
  latDeg: number, lonDeg: number,
) => { h: number; level: number } | null;

export interface PyramidPlotOptions {
  bodyId: string;
  /** The radius the leaves are built on, which sets the field's metres per degree. */
  datumM: number;
  centreLatDeg: number;
  centreLonDeg: number;
  probe: PyramidHeightProbe;
  /** The leaves' imagery; absent, the plot is drawn in its tint alone. */
  albedo?: PyramidAlbedoProbe;
  /** True: while too little of the patch has imagery drawn over it, the build
   *  answers null to be asked again, rather than mounting ground with none. */
  albedoWait?: boolean;
  /** Ground sample of a level, metres: the pyramid's own `gsd`. */
  gsdM: (level: number) => number;
  /** How much of this ground the pyramid measured rather than filled, 0..1. */
  measuredFraction: number;
  provenance: PlotMeta['provenance'];
  /** Give the frame back; the patch is sampled across frames like the survey. */
  slice: () => Promise<void>;
  /** False once the ground this build is about has been given back. */
  live: () => boolean;
  samples?: number;
  hexRadiusM?: number;
}

export interface PyramidPlotResult {
  field: PlotField;
  /** The coarsest level that answered anywhere in the patch. */
  level: number;
  /** Samples the ring had no leaf for, filled from their resolved neighbours. */
  holes: number;
}

/**
 * A patch point's body-fixed lat/lon, degrees: +x east and +z south on the plane
 * tangent to the datum sphere at the centre, the plane `siteBasis` in
 * src/atlas/farField.ts draws the mesh on. An equirectangular grid collapses at
 * a pole, where a siteless descent lands.
 */
export function patchLatLon(
  centreLatDeg: number, centreLonDeg: number, datumM: number, xM: number, zM: number,
): { lat: number; lon: number } {
  const la = (centreLatDeg * Math.PI) / 180;
  const lo = (centreLonDeg * Math.PI) / 180;
  const cl = Math.cos(la);
  const sl = Math.sin(la);
  const co = Math.cos(lo);
  const so = Math.sin(lo);
  // up * datum + east * x + south * z, on `latLonToVec3`'s axes.
  const px = cl * co * datumM - so * xM + sl * co * zM;
  const py = sl * datumM - cl * zM;
  const pz = -cl * so * datumM - co * xM - sl * so * zM;
  const r = Math.hypot(px, py, pz);
  const lat = (Math.asin(Math.max(-1, Math.min(1, py / r))) * 180) / Math.PI;
  const lon = (Math.atan2(-pz, px) * 180) / Math.PI;
  return { lat, lon: ((lon % 360) + 360) % 360 };
}

/** `patchLatLon` the other way round; null on the far hemisphere, which the plane never reaches. */
export function patchXZ(
  centreLatDeg: number, centreLonDeg: number, datumM: number, latDeg: number, lonDeg: number,
): { x: number; z: number } | null {
  const la = (centreLatDeg * Math.PI) / 180;
  const lo = (centreLonDeg * Math.PI) / 180;
  const pla = (latDeg * Math.PI) / 180;
  const plo = (lonDeg * Math.PI) / 180;
  const vx = Math.cos(pla) * Math.cos(plo);
  const vy = Math.sin(pla);
  const vz = -Math.cos(pla) * Math.sin(plo);
  const cl = Math.cos(la);
  const sl = Math.sin(la);
  const co = Math.cos(lo);
  const so = Math.sin(lo);
  const up = vx * cl * co + vy * sl - vz * cl * so;
  if (!(up > 1e-9)) return null;
  const t = datumM / up;
  return { x: t * (-vx * so - vz * co), z: t * (vx * sl * co - vy * cl - vz * sl * so) };
}

/** The class the placement carries, from what the leaves say they measured. */
function sourceFor(measuredFraction: number): SourceClass {
  if (measuredFraction >= 0.999) return 'measured';
  return measuredFraction > 0 ? 'measured+inferred' : 'coarse+synthetic';
}

/**
 * Nearest-resolved fill, rows then columns.
 *
 * A hole is a leaf the ring has not got rather than ground that is absent, so
 * the patch carries its neighbour's elevation rather than a zero that would
 * draw a pit. `PYRAMID_PLOT_MIN_RESOLVED` is what keeps this to an edge.
 */
function fillHoles(h: Float32Array, w: number, n: number): void {
  for (let y = 0; y < n; y++) {
    const row = y * w;
    let last = NaN;
    for (let x = 0; x < w; x++) {
      const v = h[row + x]!;
      if (Number.isNaN(v)) { if (!Number.isNaN(last)) h[row + x] = last; } else last = v;
    }
    last = NaN;
    for (let x = w - 1; x >= 0; x--) {
      const v = h[row + x]!;
      if (Number.isNaN(v)) { if (!Number.isNaN(last)) h[row + x] = last; } else last = v;
    }
  }
  for (let x = 0; x < w; x++) {
    let last = NaN;
    for (let y = 0; y < n; y++) {
      const v = h[y * w + x]!;
      if (Number.isNaN(v)) { if (!Number.isNaN(last)) h[y * w + x] = last; } else last = v;
    }
    last = NaN;
    for (let y = n - 1; y >= 0; y--) {
      const v = h[y * w + x]!;
      if (Number.isNaN(v)) { if (!Number.isNaN(last)) h[y * w + x] = last; } else last = v;
    }
  }
}

/**
 * Bilinear luminance of an RGBA texel grid at texel position (`s`, `t`), with
 * texel centres at integers. Texels under half alpha are unmeasured ground and
 * carry no weight; null where all four are. `table` maps a byte to the value
 * the channel is worth (a body's linear albedo decode); absent, the byte itself.
 */
export function lumaAt(img: { data: ArrayLike<number>; width: number; height: number },
  s: number, t: number, table?: ArrayLike<number>): number | null {
  const x0 = Math.floor(s);
  const y0 = Math.floor(t);
  const fx = s - x0;
  const fy = t - y0;
  let sum = 0;
  let wsum = 0;
  for (let dy = 0; dy < 2; dy++) {
    const y = Math.min(img.height - 1, Math.max(0, y0 + dy));
    const wy = dy === 0 ? 1 - fy : fy;
    for (let dx = 0; dx < 2; dx++) {
      const x = Math.min(img.width - 1, Math.max(0, x0 + dx));
      const w = wy * (dx === 0 ? 1 - fx : fx);
      const k = (y * img.width + x) * 4;
      if (w <= 0 || img.data[k + 3]! < 128) continue;
      const r = img.data[k]!;
      const g = img.data[k + 1]!;
      const b = img.data[k + 2]!;
      sum += w * (table
        ? 0.2126 * table[r]! + 0.7152 * table[g]! + 0.0722 * table[b]!
        : 0.2126 * r + 0.7152 * g + 0.0722 * b);
      wsum += w;
    }
  }
  return wsum > 0 ? sum / wsum : null;
}

/**
 * The leaves' imagery over the patch as a greyscale orthoimage, row 0 north,
 * on the same grid the height is sampled on. Null where too little of it is
 * measured, or once the build is no longer wanted; `'undrawn'` under
 * `albedoWait` while too little of it is drawn yet.
 */
async function orthoFrom(
  probe: PyramidAlbedoProbe, opts: PyramidPlotOptions, extentM: number,
): Promise<ImageBitmap | null | 'undrawn'> {
  const n = PYRAMID_PLOT_ORTHO_SAMPLES;
  const lum = new Float32Array(n * n);
  const undrawnMax = n * n * (1 - PYRAMID_PLOT_MIN_RESOLVED);
  let resolved = 0;
  let undrawn = 0;
  for (let j = 0; j < n; j++) {
    const z = ((j + 0.5) / n - 0.5) * extentM;
    for (let i = 0; i < n; i++) {
      const ll = patchLatLon(opts.centreLatDeg, opts.centreLonDeg, opts.datumM, ((i + 0.5) / n - 0.5) * extentM, z);
      const v = probe(ll.lat, ll.lon);
      if (v === undefined) undrawn++;
      if (v === null || v === undefined || !Number.isFinite(v)) {
        lum[j * n + i] = NaN;
        continue;
      }
      lum[j * n + i] = v;
      resolved++;
    }
    if (opts.albedoWait && undrawn > undrawnMax) return 'undrawn';
    await opts.slice();
    if (!opts.live()) return null;
  }
  if (resolved < n * n * PYRAMID_PLOT_MIN_RESOLVED) return null;
  if (resolved < n * n) fillHoles(lum, n, n);
  const px = new Uint8ClampedArray(n * n * 4);
  for (let k = 0; k < lum.length; k++) {
    const v = lum[k]!;
    px[k * 4] = v;
    px[k * 4 + 1] = v;
    px[k * 4 + 2] = v;
    px[k * 4 + 3] = 255;
  }
  return createImageBitmap(new ImageData(px, n, n),
    { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
}

/**
 * The patch under the landing point, as the field a claim would have given.
 *
 * The grid is +x east, +z south with the centre at the origin, like a bake's,
 * but on `patchLatLon`'s tangent plane rather than a bake's equirectangular
 * frame, and `regionXZAt` and `latLonAt` in src/atlas/plotGeometry.ts read it that way.
 *
 * Null where the ring cannot answer for the patch yet, which is the caller's
 * signal to ask again on a later frame rather than to give up.
 */
export async function buildPyramidPlotField(
  opts: PyramidPlotOptions,
): Promise<PyramidPlotResult | null> {
  const n = opts.samples ?? PYRAMID_PLOT_SAMPLES;
  const hexR = opts.hexRadiusM ?? PYRAMID_PLOT_HEX_RADIUS_M;
  // The hex's corners, so the whole parent is inside the field `sunVisible`
  // marches over and `heightAt` never clamps inside it.
  const extentM = 2 * hexR;
  const perDegLat = (opts.datumM * Math.PI) / 180;
  const perDegLon = perDegLat * Math.max(0.02,
    Math.cos((opts.centreLatDeg * Math.PI) / 180));
  const height = new Float32Array(n * n);
  let coarsest = Infinity;
  let resolved = 0;
  for (let j = 0; j < n; j++) {
    const z = ((j + 0.5) / n - 0.5) * extentM;
    for (let i = 0; i < n; i++) {
      const x = ((i + 0.5) / n - 0.5) * extentM;
      const ll = patchLatLon(opts.centreLatDeg, opts.centreLonDeg, opts.datumM, x, z);
      const got = opts.probe(ll.lat, ll.lon);
      if (got === null || !Number.isFinite(got.h)) {
        height[j * n + i] = NaN;
        continue;
      }
      height[j * n + i] = got.h;
      if (got.level < coarsest) coarsest = got.level;
      resolved++;
    }
    await opts.slice();
    if (!opts.live()) return null;
  }
  if (resolved < n * n * PYRAMID_PLOT_MIN_RESOLVED) return null;
  const holes = n * n - resolved;
  if (holes > 0) fillHoles(height, n, n);
  let minM = Infinity;
  let maxM = -Infinity;
  for (const h of height) {
    if (h < minM) minM = h;
    if (h > maxM) maxM = h;
  }
  // A patch with no relief at all would divide by zero in the shader's own
  // normalisation; a millimetre of range is flat at any exaggeration.
  const reliefM = Math.max(maxM - minM, 0.001);
  const norm = new Float32Array(n * n);
  for (let k = 0; k < norm.length; k++) norm[k] = (height[k]! - minM) / reliefM;
  const buffers: FieldBuffers = { norm, height, mips: buildHeightMips(norm, n, n) };
  const ortho = opts.albedo ? await orthoFrom(opts.albedo, opts, extentM) : null;
  if (ortho === 'undrawn') return null;
  if (!opts.live()) {
    ortho?.close();
    return null;
  }
  const level = Number.isFinite(coarsest) ? coarsest : 0;
  const placement: HexPlacement = {
    q: 0, r: 0, x_m: 0, z_m: 0,
    measured_fraction: opts.measuredFraction,
    source: sourceFor(opts.measuredFraction),
  };
  const meta: PlotMeta = {
    slug: `${opts.bodyId}-pyramid`,
    pyramid: true,
    hexes: [placement],
    hex: {
      radius_m: hexR,
      across_flats_m: Math.sqrt(3) * hexR,
      across_corners_m: 2 * hexR,
      area_km2: (2.598076 * hexR * hexR) / 1e6,
      orientation: 'pointy-z',
      measured_fraction: opts.measuredFraction,
    },
    bbox_m: { width: extentM, height: extentM },
    center_lat_deg: opts.centreLatDeg,
    center_lon_deg: opts.centreLonDeg,
    frame: { m_per_deg_lat: perDegLat, m_per_deg_lon: perDegLon },
    elevation: {
      min_m: minM, max_m: maxM, relief_m: reliefM,
      datum: `${opts.bodyId} pyramid datum`,
    },
    height_texture: { file: '', size: n, width: n, height: n, bits: 32 },
    // No file: the bitmap rides on `albedoPromise`, which `ensurePlotMesh` reads first.
    albedo_texture: ortho ? { file: '', size: PYRAMID_PLOT_ORTHO_SAMPLES } : null,
    source_resolution_m_per_px: opts.gsdM(level),
    effective_resolution_m_per_px: extentM / n,
    rocks: null,
    provenance: opts.provenance,
  };
  const field = new PlotField(meta, buffers);
  if (ortho) field.albedoPromise = Promise.resolve(ortho);
  return { field, level, holes };
}

/** True where this field was built here rather than loaded from a bake. */
export function isPyramidPlot(meta: PlotMeta): boolean {
  return meta.pyramid === true;
}
